import { useTheme } from "@material-ui/core"
import { MembershipViewModel } from "../api/membership"
import type { Translations } from "../hooks/use-settings-page-translations"
import { useMembershipPermission } from "../../permissions/hooks/use-memberhip-permission"
import { useOptions } from "../../options/hooks/use-options"
import { useMembershipActions } from "../hooks/use-membership-actions"
import { Typography, Button } from "@material-ui/core"
import { alpha } from "@material-ui/core/styles"
import Stack from "../../components/Stack"
import { ChevronRight } from "react-feather"
import InvitationEmailLanguageSwitch from "./InvitationEmailLanguageSwitch"
import { StyledAccordion } from "./StyledAccordion"
import { MembershipPermissionManager } from "./MembershipPermissionManager"
import { Alert } from "@material-ui/lab"
import { FormEvent, useState } from "react"
import { useInviteMemberIntent, useScrollToInviteFormOnInviteMemberIntent } from "../hooks/use-invite-member-intent"
import { BillingAlert } from "./BillingAlert"
import { useLanguage } from "../../i18n/use-language"
import { CostEstimationScenario } from "../../organisations/api"
import { useSettingsPageTranslations } from "../hooks/use-settings-page-translations"

type MembershipInvitationFormProps = {
  membership: MembershipViewModel
  translations: Translations
}

const INVITATION_FORM_ID = "membership-invitation-form"

const getCostEstimationScenarios = (
  membership: MembershipViewModel,
  abilitiesCount: number
): CostEstimationScenario[] => {
  return [
    {
      type: "addMemberClone",
      sourceMemberId: membership.id,
      count: 1,
      // @ts-expect-error - to invalidate cache when role or abilities change, as role is a factor in cost estimation
      _invalidateKey: `${membership.role}-${abilitiesCount}`,
    },
  ]
}

const useMembershipInvitationForm = ({ membership }: MembershipInvitationFormProps) => {
  const actions = useMembershipActions()
  const language = useLanguage()
  const [invitationLanguage, setInvitationLanguage] = useState(language)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    actions.sendInvitation({ membershipId: membership.id, language: invitationLanguage })
  }

  return {
    invitationLanguage,
    setInvitationLanguage,
    handleSubmit,
  }
}

export const MembershipInvitationForm = ({
  membership,
  translations: baseTranslations,
}: MembershipInvitationFormProps) => {
  useScrollToInviteFormOnInviteMemberIntent(INVITATION_FORM_ID)
  const theme = useTheme()
  const { permission, updateAssociations, updateAbilitiesInResources, updateAbilities, copyPermissions } =
    useMembershipPermission(membership.id)
  const { customerOptions, membershipOptions, supplierOptions } = useOptions(membership.orgId)
  const { invitationLanguage, setInvitationLanguage, handleSubmit } = useMembershipInvitationForm({
    membership,
    translations: baseTranslations,
  })
  const { isInviteMemberIntent, setInviteMemberIntent } = useInviteMemberIntent()
  const accessLevel = membership.isStandardMember
    ? baseTranslations.standardMemberLabel
    : baseTranslations.limitedMemberLabel
  const translations = useSettingsPageTranslations({
    name: membership.fullname,
    member: membership.firstname,
    orgName: membership.orgName,
    fullname: membership.fullname,
    accessLevel: accessLevel.toLocaleLowerCase(),
  })
  const emailError = isInviteMemberIntent && !membership.userEmail ? translations.provideValidEmailToInvite : null

  const isEmailError = Boolean(emailError)

  return (
    <>
      {membership.hasRejectedInvitation ? (
        <Alert severity="warning" style={{ marginTop: 12, marginBottom: 32 }} variant="filled">
          {translations.memberRejectedMessage}
        </Alert>
      ) : null}

      <Typography variant="h6" gutterBottom style={{ marginBottom: 24 }}>
        {translations.invitationHeader}
      </Typography>

      <form onSubmit={handleSubmit} id={INVITATION_FORM_ID}>
        <Stack spacing={3}>
          <InvitationEmailLanguageSwitch
            language={invitationLanguage}
            onChangeLanguage={setInvitationLanguage}
            translations={translations}
            shouldShowLanguageSwitch={true}
          />

          <div style={{ backgroundColor: alpha(theme.palette.primary.main, 0.05), padding: 16, borderRadius: 4 }}>
            <Typography variant="body2" gutterBottom>
              {translations.accessLevelInfo}
            </Typography>
            <StyledAccordion title={translations.customizeAccess}>
              <MembershipPermissionManager
                membership={membership}
                permission={permission}
                copyPermissions={copyPermissions}
                setAbilitiesInResources={updateAbilitiesInResources}
                setAssociations={updateAssociations}
                updateAbilities={(updates) => updateAbilities({ orgId: membership.orgId, ...updates })}
                customerOptions={customerOptions}
                supplierOptions={supplierOptions}
                membershipOptions={membershipOptions}
                translations={translations}
              />
            </StyledAccordion>
          </div>

          <BillingAlert
            orgId={membership.orgId}
            costEstimationScenarios={getCostEstimationScenarios(membership, permission.abilities.length)}
            translations={translations}
          />

          <Button
            disabled={isEmailError}
            onClick={() => setInviteMemberIntent(membership.id)}
            type="submit"
            variant="contained"
            color="primary"
            endIcon={<ChevronRight size={20} />}
          >
            {membership.hasRejectedInvitation ? translations.sendInvitationAgain : translations.sendInvitation}
          </Button>
        </Stack>
      </form>
    </>
  )
}
